import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { STAGE_ORDER } from '../../utils/formatters';

const COLORS = [
  '#1e3a5f', '#c9952a', '#2d7a4f', '#c0392b',
  '#8e44ad', '#16a085', '#d35400', '#2980b9',
  '#27ae60', '#e74c3c',
];

function advancedBeyondPIF(deal) {
  return STAGE_ORDER.indexOf(deal.current_stage) > 0;
}

function advRate(dealsArr) {
  if (!dealsArr.length) return null;
  const adv = dealsArr.filter(advancedBeyondPIF).length;
  return Math.round((adv / dealsArr.length) * 100);
}

export default function WomenLedView({ deals }) {
  const womenLed = deals.filter(d => d.is_women_led === true);
  const notWomenLed = deals.filter(d => d.is_women_led === false);
  const nullCount = deals.filter(d => d.is_women_led == null).length;
  const denominator = womenLed.length + notWomenLed.length;
  const womenLedPct = denominator > 0 ? Math.round((womenLed.length / denominator) * 100) : null;
  const womenRate = advRate(womenLed);
  const otherRate = advRate(notWomenLed);

  // Sector breakdown for women-led deals
  const sectorMap = {};
  womenLed.forEach(d => {
    const s = d.sector || 'Unknown';
    sectorMap[s] = (sectorMap[s] || 0) + 1;
  });
  const sectorData = Object.entries(sectorMap)
    .map(([sector, count]) => ({ sector, count, name: sector, value: count }))
    .sort((a, b) => b.count - a.count);

  const metricCard = (label, value, sub) => (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius)', padding: '20px 24px', textAlign: 'center',
    }}>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{label}</p>
      <p style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-accent)' }}>
        {value !== null ? `${value}%` : '—'}
      </p>
      {sub && <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>{sub}</p>}
    </div>
  );

  return (
    <div>
      {/* Headline metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {metricCard(
          '% of pipeline that is women-led',
          womenLedPct,
          `${womenLed.length} of ${denominator} deals (excl. ${nullCount} legacy)`
        )}
        {metricCard(
          'Advancement rate — Women-led',
          womenRate,
          `${womenLed.filter(advancedBeyondPIF).length} of ${womenLed.length} deals advanced`
        )}
        {metricCard(
          'Advancement rate — Other',
          otherRate,
          `${notWomenLed.filter(advancedBeyondPIF).length} of ${notWomenLed.length} deals advanced`
        )}
      </div>

      {/* Sector distribution of women-led deals */}
      {sectorData.length > 0 && (
        <div className="card mb-4">
          <div className="card-header"><h2>Sector Distribution — Women-Led Companies</h2></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sectorData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {sectorData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} deals`, 'Count']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Legacy note */}
      {nullCount > 0 && (
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 8 }}>
          {nullCount} deal{nullCount !== 1 ? 's' : ''} submitted before the women-led field was introduced
          {nullCount !== 1 ? ' are' : ' is'} excluded from this analysis.
        </p>
      )}

      {womenLed.length === 0 && denominator === 0 && (
        <p className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
          No deals with women-led data yet. This view will populate once deals include the women-led field.
        </p>
      )}
    </div>
  );
}
